/**
 * Revenue Forecasting POC – Enterprise Redesign
 * Route: /revenue-forecasting-poc
 *
 * Uses the same Redux slices / API calls as the existing page.
 * Finance view  : BU → Department → Client
 * Sales view    : Client → BU → Department
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button, Select, Input, Spin, notification, Tooltip, Pagination,
  Drawer, Tag, Skeleton
} from 'antd';
import {
  BarChartOutlined, DollarOutlined, RiseOutlined, FundOutlined,
  FilterOutlined, DownloadOutlined, ReloadOutlined, SearchOutlined,
  CaretRightOutlined, CaretDownOutlined, AppstoreOutlined, TableOutlined,
  TrophyOutlined, WarningOutlined, SwapOutlined, CloseOutlined,
  ArrowUpOutlined, ArrowDownOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchForecastRevenue, fetchSalesViewRevenue, fetchFilterOptions,
  exportFinanceViewAsExcel, exportSalesViewAsExcel
} from '../store/reducers/revenueForecastSlice';
import { AppDispatch, RootState } from '../store/store';
import { getUserRole, rejectedApiMessage } from '../utilities/CommonHelper';
import { getUserAccessData } from '../utilities/sessionHelper';
import { transformOldToNewStructure } from '../utilities/revenueForecastTransformer';
import './RevenueForecastingPOC.css';

const { Option } = Select;

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmt = (n: number): string => {
  if (!n) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtFull = (n: number): string =>
  '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const realizationColor = (pct: number): string => {
  if (pct >= 80) return '#059669';
  if (pct >= 50) return '#d97706';
  return '#dc2626';
};
const realizationClass = (pct: number): string => {
  if (pct >= 80) return 'green';
  if (pct >= 50) return 'yellow';
  return 'red';
};
const realizationBadge = (pct: number): string => {
  if (pct >= 80) return 'green';
  if (pct >= 50) return 'yellow';
  return 'red';
};

const getCurrentFiscalInfo = () => {
  const month = new Date().getMonth() + 1;
  const year = new Date().getFullYear();
  // Fiscal year: Apr–Jun=Q1, Jul–Sep=Q2, Oct–Dec=Q3, Jan–Mar=Q4
  let q = 1;
  if (month >= 4 && month <= 6) q = 1;
  else if (month >= 7 && month <= 9) q = 2;
  else if (month >= 10 && month <= 12) q = 3;
  else q = 4;
  // Fiscal year starts April; Jan–Mar belongs to previous FY
  const fiscalYear = month >= 4 ? year : year - 1;
  return { year: fiscalYear, quarter: q };
};

/* ─────────────────────────────────────────────
   SPARKLINE (SVG mini bar chart)
───────────────────────────────────────────── */
const Sparkline: React.FC<{ values: number[]; color?: string }> = ({
  values, color = '#667eea'
}) => {
  const W = 72, H = 22;
  const max = Math.max(...values, 1);
  const barW = Math.max(4, (W - (values.length - 1) * 2) / values.length);
  return (
    <svg width={W} height={H} className="rfpoc-sparkline">
      {values.map((v, i) => {
        const h = Math.max(2, (v / max) * (H - 4));
        const x = i * (barW + 2);
        return (
          <rect
            key={i}
            x={x}
            y={H - h - 2}
            width={barW}
            height={h}
            rx={1}
            fill={color}
            opacity={0.7 + (i === values.length - 1 ? 0.3 : 0)}
          />
        );
      })}
    </svg>
  );
};

/* ─────────────────────────────────────────────
   REALIZATION CELL
───────────────────────────────────────────── */
const RealizationCell: React.FC<{ actual: number; total: number }> = ({ actual, total }) => {
  const pct = total > 0 ? Math.min(((actual / total) * 100), 999) : 0;
  const display = total > 0 ? `${pct.toFixed(1)}%` : '—';
  return (
    <div className="rfpoc-real-bar-wrap">
      <div className="rfpoc-real-bar-bg">
        <div
          className="rfpoc-real-bar-fill"
          style={{ width: `${Math.min(pct, 100)}%`, background: realizationColor(pct) }}
        />
      </div>
      <span className={`rfpoc-real-pct ${realizationClass(pct)}`}>{display}</span>
    </div>
  );
};

/* ─────────────────────────────────────────────
   QUARTER CHART COMPONENT
───────────────────────────────────────────── */
interface QuarterEntry {
  quarter: string;
  status?: string;
  projectRevenue: number;
  pipelineRevenue: number;
  total: number;
  actualRevenue: number;
}

const QuarterChart: React.FC<{ quarters: QuarterEntry[]; selectedQuarters: string[] }> = ({
  quarters, selectedQuarters
}) => {
  if (!quarters || quarters.length === 0) return null;

  const visible = selectedQuarters.length
    ? quarters.filter(q => selectedQuarters.includes(q.quarter))
    : quarters;

  const maxVal = Math.max(...visible.map(q => q.total), 1);

  return (
    <div className="rfpoc-chart-section">
      <h3><BarChartOutlined style={{ marginRight: 8, color: '#667eea' }} />Revenue by Quarter</h3>
      <div className="rfpoc-quarter-chart">
        {visible.map((q) => {
          const projH = (q.projectRevenue / maxVal) * 80;
          const pipeH = (q.pipelineRevenue / maxVal) * 80;
          const actH = (q.actualRevenue / maxVal) * 80;
          const isCurrent = q.status === 'Current';
          return (
            <Tooltip
              key={q.quarter}
              title={
                <div>
                  <div><b>{q.quarter}{isCurrent ? ' (Current)' : ''}</b></div>
                  <div>Projects: {fmt(q.projectRevenue)}</div>
                  <div>Pipeline: {fmt(q.pipelineRevenue)}</div>
                  <div>Actuals: {fmt(q.actualRevenue)}</div>
                  <div><b>Total: {fmt(q.total)}</b></div>
                </div>
              }
            >
              <div className="rfpoc-bar-group">
                <div className="rfpoc-bar-stack">
                  {projH > 0 && (
                    <div className="rfpoc-bar-seg project" style={{ height: projH }} />
                  )}
                  {pipeH > 0 && (
                    <div className="rfpoc-bar-seg pipeline" style={{ height: pipeH }} />
                  )}
                  {actH > 0 && (
                    <div className="rfpoc-bar-seg actual" style={{ height: actH }} />
                  )}
                </div>
                <div className={`rfpoc-bar-label ${isCurrent ? 'current' : ''}`}>
                  {q.quarter.replace(' ', '\n')}
                  {isCurrent && <span style={{ display: 'block', color: '#764ba2', fontSize: 9 }}>NOW</span>}
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>
      <div className="rfpoc-chart-legend">
        <div className="rfpoc-legend-item">
          <div className="rfpoc-legend-dot" style={{ background: '#667eea' }} />
          Projects
        </div>
        <div className="rfpoc-legend-item">
          <div className="rfpoc-legend-dot" style={{ background: '#10b981' }} />
          Pipeline
        </div>
        <div className="rfpoc-legend-item">
          <div className="rfpoc-legend-dot" style={{ background: '#f59e0b' }} />
          Actuals
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   FINANCE TABLE ROW (recursive)
───────────────────────────────────────────── */
interface BUData {
  type: string;
  name: string;
  totals: { projectRevenue: number; pipelineRevenue: number; total: number; actualRevenue: number };
  quarters: QuarterEntry[];
  departments?: DeptData[];
  businessUnits?: BUData[];
}
interface DeptData {
  type: string;
  name: string;
  totals: { projectRevenue: number; pipelineRevenue: number; total: number; actualRevenue: number };
  quarters: QuarterEntry[];
  clients?: ClientData[];
  departments?: DeptData[];
}
interface ClientData {
  type: string;
  name: string;
  totals: { projectRevenue: number; pipelineRevenue: number; total: number; actualRevenue: number };
  quarters: QuarterEntry[];
}

const FinanceRows: React.FC<{
  bu: BUData;
  searchStr: string;
  selectedQuarters: string[];
}> = ({ bu, searchStr, selectedQuarters }) => {
  const [open, setOpen] = useState(false);
  const hasDepts = bu.departments && bu.departments.length > 0;

  const sparkData = (bu.quarters || []).map(q => q.total);
  const matchesBU = bu.name.toLowerCase().includes(searchStr.toLowerCase());
  const deptMatches = (bu.departments || []).filter(d =>
    d.name?.toLowerCase().includes(searchStr.toLowerCase()) ||
    (d.clients || []).some(c => c.name?.toLowerCase().includes(searchStr.toLowerCase()))
  );
  if (searchStr && !matchesBU && deptMatches.length === 0) return null;
  const shouldOpen = searchStr ? (open || deptMatches.length > 0) : open;

  const qKeys = selectedQuarters.length
    ? selectedQuarters
    : (bu.quarters || []).map(q => q.quarter);
  const visibleQuarters = (bu.quarters || []).filter(q =>
    !selectedQuarters.length || selectedQuarters.includes(q.quarter)
  );

  return (
    <>
      <tr className="row-bu">
        <td className="sticky-left">
          <div className="rfpoc-name-cell">
            {hasDepts
              ? <button className="rfpoc-expand-btn" onClick={() => setOpen(!shouldOpen)}>
                  {shouldOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </button>
              : <div style={{ width: 20 }} />
            }
            <span className="rfpoc-type-badge bu">BU</span>
            <Tooltip title={bu.name}>
              <span className="rfpoc-name-text bu-level">{bu.name}</span>
            </Tooltip>
          </div>
        </td>
        <td className="rfpoc-rev-cell project-col">{fmt(bu.totals?.projectRevenue)}</td>
        <td className="rfpoc-rev-cell pipeline-col">{fmt(bu.totals?.pipelineRevenue)}</td>
        <td className="rfpoc-rev-cell total-col">{fmt(bu.totals?.total)}</td>
        <td className="rfpoc-rev-cell actual-col">{fmt(bu.totals?.actualRevenue)}</td>
        <td className="rfpoc-real-cell">
          <RealizationCell actual={bu.totals?.actualRevenue} total={bu.totals?.total} />
        </td>
        <td className="rfpoc-sparkline-cell">
          <Sparkline values={sparkData.length ? sparkData : [0]} color="#667eea" />
        </td>
        {visibleQuarters.map(q => (
          <React.Fragment key={q.quarter}>
            <td className="rfpoc-rev-cell project-col" style={{ borderLeft: '2px solid #e2e8f0' }}>{fmt(q.projectRevenue)}</td>
            <td className="rfpoc-rev-cell pipeline-col">{fmt(q.pipelineRevenue)}</td>
            <td className="rfpoc-rev-cell total-col">{fmt(q.total)}</td>
            <td className="rfpoc-rev-cell actual-col">{fmt(q.actualRevenue)}</td>
          </React.Fragment>
        ))}
      </tr>

      {shouldOpen && (bu.departments || []).map((dept, di) => (
        <DeptRow
          key={`${bu.name}-dept-${di}`}
          dept={dept}
          indent={1}
          searchStr={searchStr}
          selectedQuarters={selectedQuarters}
          allQuarters={bu.quarters || []}
        />
      ))}
    </>
  );
};

const DeptRow: React.FC<{
  dept: DeptData;
  indent: number;
  searchStr: string;
  selectedQuarters: string[];
  allQuarters: QuarterEntry[];
}> = ({ dept, indent, searchStr, selectedQuarters, allQuarters }) => {
  const [open, setOpen] = useState(false);
  if (!dept?.name) return null;

  const hasClients = dept.clients && dept.clients.length > 0;
  const matchesDept = dept.name.toLowerCase().includes(searchStr.toLowerCase());
  const clientMatches = (dept.clients || []).filter(c =>
    c.name?.toLowerCase().includes(searchStr.toLowerCase())
  );
  if (searchStr && !matchesDept && clientMatches.length === 0) return null;
  const shouldOpen = searchStr ? (open || clientMatches.length > 0) : open;

  const sparkData = (dept.quarters || allQuarters).map(q => q.total || 0);
  const visibleQuarters = allQuarters.filter(q =>
    !selectedQuarters.length || selectedQuarters.includes(q.quarter)
  );

  return (
    <>
      <tr className="row-dept">
        <td className="sticky-left">
          <div className={`rfpoc-name-cell rfpoc-indent-${indent}`}>
            {hasClients
              ? <button className="rfpoc-expand-btn" onClick={() => setOpen(!shouldOpen)}>
                  {shouldOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </button>
              : <div style={{ width: 20 }} />
            }
            <span className="rfpoc-type-badge dept">Dept</span>
            <Tooltip title={dept.name}>
              <span className="rfpoc-name-text dept-level">{dept.name}</span>
            </Tooltip>
          </div>
        </td>
        <td className="rfpoc-rev-cell project-col">{fmt(dept.totals?.projectRevenue)}</td>
        <td className="rfpoc-rev-cell pipeline-col">{fmt(dept.totals?.pipelineRevenue)}</td>
        <td className="rfpoc-rev-cell total-col">{fmt(dept.totals?.total)}</td>
        <td className="rfpoc-rev-cell actual-col">{fmt(dept.totals?.actualRevenue)}</td>
        <td className="rfpoc-real-cell">
          <RealizationCell actual={dept.totals?.actualRevenue} total={dept.totals?.total} />
        </td>
        <td className="rfpoc-sparkline-cell">
          <Sparkline values={sparkData.length ? sparkData : [0]} color="#3b82f6" />
        </td>
        {visibleQuarters.map(q => {
          const dq = (dept.quarters || []).find(dqe => dqe.quarter === q.quarter);
          return (
            <React.Fragment key={q.quarter}>
              <td className="rfpoc-rev-cell project-col" style={{ borderLeft: '2px solid #e2e8f0' }}>{fmt(dq?.projectRevenue || 0)}</td>
              <td className="rfpoc-rev-cell pipeline-col">{fmt(dq?.pipelineRevenue || 0)}</td>
              <td className="rfpoc-rev-cell total-col">{fmt(dq?.total || 0)}</td>
              <td className="rfpoc-rev-cell actual-col">{fmt(dq?.actualRevenue || 0)}</td>
            </React.Fragment>
          );
        })}
      </tr>

      {shouldOpen && (dept.clients || []).map((client, ci) => (
        <ClientRow
          key={`${dept.name}-client-${ci}`}
          client={client}
          indent={2}
          searchStr={searchStr}
          selectedQuarters={selectedQuarters}
          allQuarters={allQuarters}
        />
      ))}
    </>
  );
};

const ClientRow: React.FC<{
  client: ClientData;
  indent: number;
  searchStr: string;
  selectedQuarters: string[];
  allQuarters: QuarterEntry[];
}> = ({ client, indent, searchStr, selectedQuarters, allQuarters }) => {
  if (!client?.name) return null;
  if (searchStr && !client.name.toLowerCase().includes(searchStr.toLowerCase())) return null;

  const sparkData = (client.quarters || allQuarters).map(q => q.total || 0);
  const visibleQuarters = allQuarters.filter(q =>
    !selectedQuarters.length || selectedQuarters.includes(q.quarter)
  );

  return (
    <tr className="row-client">
      <td className="sticky-left">
        <div className={`rfpoc-name-cell rfpoc-indent-${indent}`}>
          <div style={{ width: 20 }} />
          <span className="rfpoc-type-badge client">Client</span>
          <Tooltip title={client.name}>
            <span className="rfpoc-name-text client-level">{client.name}</span>
          </Tooltip>
        </div>
      </td>
      <td className="rfpoc-rev-cell project-col">{fmt(client.totals?.projectRevenue)}</td>
      <td className="rfpoc-rev-cell pipeline-col">{fmt(client.totals?.pipelineRevenue)}</td>
      <td className="rfpoc-rev-cell total-col">{fmt(client.totals?.total)}</td>
      <td className="rfpoc-rev-cell actual-col">{fmt(client.totals?.actualRevenue)}</td>
      <td className="rfpoc-real-cell">
        <RealizationCell actual={client.totals?.actualRevenue} total={client.totals?.total} />
      </td>
      <td className="rfpoc-sparkline-cell">
        <Sparkline values={sparkData.length ? sparkData : [0]} color="#10b981" />
      </td>
      {visibleQuarters.map(q => {
        const cq = (client.quarters || []).find(cqe => cqe.quarter === q.quarter);
        return (
          <React.Fragment key={q.quarter}>
            <td className="rfpoc-rev-cell project-col" style={{ borderLeft: '2px solid #e2e8f0' }}>{fmt(cq?.projectRevenue || 0)}</td>
            <td className="rfpoc-rev-cell pipeline-col">{fmt(cq?.pipelineRevenue || 0)}</td>
            <td className="rfpoc-rev-cell total-col">{fmt(cq?.total || 0)}</td>
            <td className="rfpoc-rev-cell actual-col">{fmt(cq?.actualRevenue || 0)}</td>
          </React.Fragment>
        );
      })}
    </tr>
  );
};

/* ─────────────────────────────────────────────
   SALES TABLE ROW (Client → BU → Dept)
───────────────────────────────────────────── */
interface SalesClientData {
  type: string;
  name: string;
  totals: { projectRevenue: number; pipelineRevenue: number; total: number; actualRevenue: number };
  quarters: QuarterEntry[];
  businessUnits?: BUData[];
}

const SalesClientRow: React.FC<{
  client: SalesClientData;
  searchStr: string;
  selectedQuarters: string[];
}> = ({ client, searchStr, selectedQuarters }) => {
  const [open, setOpen] = useState(false);
  const hasBUs = client.businessUnits && client.businessUnits.length > 0;

  const matchesClient = client.name?.toLowerCase().includes(searchStr.toLowerCase());
  const buMatches = (client.businessUnits || []).filter(bu =>
    bu.name?.toLowerCase().includes(searchStr.toLowerCase()) ||
    (bu.departments || []).some(d => d.name?.toLowerCase().includes(searchStr.toLowerCase()))
  );
  if (searchStr && !matchesClient && buMatches.length === 0) return null;
  const shouldOpen = searchStr ? (open || buMatches.length > 0) : open;

  const sparkData = (client.quarters || []).map(q => q.total || 0);
  const visibleQuarters = (client.quarters || []).filter(q =>
    !selectedQuarters.length || selectedQuarters.includes(q.quarter)
  );

  return (
    <>
      <tr className="row-bu">
        <td className="sticky-left">
          <div className="rfpoc-name-cell">
            {hasBUs
              ? <button className="rfpoc-expand-btn" onClick={() => setOpen(!shouldOpen)}>
                  {shouldOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </button>
              : <div style={{ width: 20 }} />
            }
            <span className="rfpoc-type-badge client">Client</span>
            <Tooltip title={client.name}>
              <span className="rfpoc-name-text bu-level">{client.name}</span>
            </Tooltip>
          </div>
        </td>
        <td className="rfpoc-rev-cell project-col">{fmt(client.totals?.projectRevenue)}</td>
        <td className="rfpoc-rev-cell pipeline-col">{fmt(client.totals?.pipelineRevenue)}</td>
        <td className="rfpoc-rev-cell total-col">{fmt(client.totals?.total)}</td>
        <td className="rfpoc-rev-cell actual-col">{fmt(client.totals?.actualRevenue)}</td>
        <td className="rfpoc-real-cell">
          <RealizationCell actual={client.totals?.actualRevenue} total={client.totals?.total} />
        </td>
        <td className="rfpoc-sparkline-cell">
          <Sparkline values={sparkData.length ? sparkData : [0]} color="#10b981" />
        </td>
        {visibleQuarters.map(q => (
          <React.Fragment key={q.quarter}>
            <td className="rfpoc-rev-cell project-col" style={{ borderLeft: '2px solid #e2e8f0' }}>{fmt(q.projectRevenue)}</td>
            <td className="rfpoc-rev-cell pipeline-col">{fmt(q.pipelineRevenue)}</td>
            <td className="rfpoc-rev-cell total-col">{fmt(q.total)}</td>
            <td className="rfpoc-rev-cell actual-col">{fmt(q.actualRevenue)}</td>
          </React.Fragment>
        ))}
      </tr>

      {shouldOpen && (client.businessUnits || []).map((bu, bi) => (
        <SalesBURow
          key={`${client.name}-bu-${bi}`}
          bu={bu}
          searchStr={searchStr}
          selectedQuarters={selectedQuarters}
          allQuarters={client.quarters || []}
        />
      ))}
    </>
  );
};

const SalesBURow: React.FC<{
  bu: BUData;
  searchStr: string;
  selectedQuarters: string[];
  allQuarters: QuarterEntry[];
}> = ({ bu, searchStr, selectedQuarters, allQuarters }) => {
  const [open, setOpen] = useState(false);
  if (!bu?.name) return null;
  const hasDepts = bu.departments && bu.departments.length > 0;
  const visibleQuarters = allQuarters.filter(q =>
    !selectedQuarters.length || selectedQuarters.includes(q.quarter)
  );
  const buQuarterMap = new Map((bu.quarters || []).map(q => [q.quarter, q]));

  return (
    <>
      <tr className="row-dept">
        <td className="sticky-left">
          <div className="rfpoc-name-cell rfpoc-indent-1">
            {hasDepts
              ? <button className="rfpoc-expand-btn" onClick={() => setOpen(!open)}>
                  {open ? <CaretDownOutlined /> : <CaretRightOutlined />}
                </button>
              : <div style={{ width: 20 }} />
            }
            <span className="rfpoc-type-badge bu">BU</span>
            <Tooltip title={bu.name}>
              <span className="rfpoc-name-text dept-level">{bu.name}</span>
            </Tooltip>
          </div>
        </td>
        <td className="rfpoc-rev-cell project-col">{fmt(bu.totals?.projectRevenue)}</td>
        <td className="rfpoc-rev-cell pipeline-col">{fmt(bu.totals?.pipelineRevenue)}</td>
        <td className="rfpoc-rev-cell total-col">{fmt(bu.totals?.total)}</td>
        <td className="rfpoc-rev-cell actual-col">{fmt(bu.totals?.actualRevenue)}</td>
        <td className="rfpoc-real-cell">
          <RealizationCell actual={bu.totals?.actualRevenue} total={bu.totals?.total} />
        </td>
        <td className="rfpoc-sparkline-cell">
          <Sparkline values={(bu.quarters || []).map(q => q.total || 0).filter(v => v > 0).length ? (bu.quarters || []).map(q => q.total || 0) : [0]} color="#667eea" />
        </td>
        {visibleQuarters.map(q => {
          const bq = buQuarterMap.get(q.quarter);
          return (
            <React.Fragment key={q.quarter}>
              <td className="rfpoc-rev-cell project-col" style={{ borderLeft: '2px solid #e2e8f0' }}>{bq ? fmt(bq.projectRevenue) : '—'}</td>
              <td className="rfpoc-rev-cell pipeline-col">{bq ? fmt(bq.pipelineRevenue) : '—'}</td>
              <td className="rfpoc-rev-cell total-col">{bq ? fmt(bq.total) : '—'}</td>
              <td className="rfpoc-rev-cell actual-col">{bq ? fmt(bq.actualRevenue) : '—'}</td>
            </React.Fragment>
          );
        })}
      </tr>

      {open && (bu.departments || []).map((dept, di) => (
        dept?.name ? (
          <tr key={`${bu.name}-dept-${di}`} className="row-client">
            <td className="sticky-left">
              <div className="rfpoc-name-cell rfpoc-indent-2">
                <div style={{ width: 20 }} />
                <span className="rfpoc-type-badge dept">Dept</span>
                <Tooltip title={dept.name}>
                  <span className="rfpoc-name-text client-level">{dept.name}</span>
                </Tooltip>
              </div>
            </td>
            <td className="rfpoc-rev-cell project-col">{fmt(dept.totals?.projectRevenue)}</td>
            <td className="rfpoc-rev-cell pipeline-col">{fmt(dept.totals?.pipelineRevenue)}</td>
            <td className="rfpoc-rev-cell total-col">{fmt(dept.totals?.total)}</td>
            <td className="rfpoc-rev-cell actual-col">{fmt(dept.totals?.actualRevenue)}</td>
            <td className="rfpoc-real-cell">
              <RealizationCell actual={dept.totals?.actualRevenue} total={dept.totals?.total} />
            </td>
            <td className="rfpoc-sparkline-cell">
              <Sparkline values={(dept.quarters || []).map((dq: QuarterEntry) => dq.total || 0).filter((v: number) => v > 0).length ? (dept.quarters || []).map((dq: QuarterEntry) => dq.total || 0) : [0]} color="#3b82f6" />
            </td>
            {visibleQuarters.map(q => {
              const dq = (dept.quarters || []).find((x: QuarterEntry) => x.quarter === q.quarter);
              return (
                <React.Fragment key={q.quarter}>
                  <td className="rfpoc-rev-cell project-col" style={{ borderLeft: '2px solid #e2e8f0' }}>{dq ? fmt(dq.projectRevenue) : '—'}</td>
                  <td className="rfpoc-rev-cell pipeline-col">{dq ? fmt(dq.pipelineRevenue) : '—'}</td>
                  <td className="rfpoc-rev-cell total-col">{dq ? fmt(dq.total) : '—'}</td>
                  <td className="rfpoc-rev-cell actual-col">{dq ? fmt(dq.actualRevenue) : '—'}</td>
                </React.Fragment>
              );
            })}
          </tr>
        ) : null
      ))}
    </>
  );
};

/* ─────────────────────────────────────────────
   KPI CARD
───────────────────────────────────────────── */
interface KPICardProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  iconBg: string;
  badge?: { text: string; type: 'green' | 'yellow' | 'red' };
  loading?: boolean;
}
const KPICard: React.FC<KPICardProps> = ({ label, value, sub, icon, iconBg, badge, loading }) => (
  <div className="rfpoc-kpi-card">
    <div className="rfpoc-kpi-icon" style={{ background: iconBg }}>{icon}</div>
    <div className="rfpoc-kpi-label">{label}</div>
    {loading
      ? <Skeleton.Input active size="small" style={{ width: 90, height: 24 }} />
      : <div className="rfpoc-kpi-value">{value}</div>
    }
    {(sub || badge) && (
      <div className="rfpoc-kpi-sub">
        {sub && <span>{sub}</span>}
        {badge && <span className={`rfpoc-kpi-badge ${badge.type}`}>{badge.text}</span>}
      </div>
    )}
  </div>
);

/* ─────────────────────────────────────────────
   MAIN PAGE COMPONENT
───────────────────────────────────────────── */
const RevenueForecastingPOC: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { year: currentYear, quarter: currentQuarter } = getCurrentFiscalInfo();

  /* Redux state */
  const {
    data: forecastData, loading, error,
    salesData, salesLoading, salesError,
    filterOptions, filterOptionsLoading,
    exportLoading
  } = useSelector((s: RootState) => s.revenueForecast);

  /* Role checks — use toLowerCase().includes() to match any variant */
  const userAccessData = getUserAccessData();
  const role = (getUserRole(userAccessData?.roleAccessMap) || '').toLowerCase();
  const isFinanceRole    = role.includes('finance') || role.includes('admin') || role.includes('superuser') || role.includes('super user');
  const isSalesRole      = role.includes('sales')   || role.includes('admin') || role.includes('superuser') || role.includes('super user');
  const isAdminOrSuperuser = role.includes('admin')  || role.includes('superuser') || role.includes('super user');

  /* View */
  const [activeView, setActiveView] = useState<'finance' | 'sales'>(
    isFinanceRole ? 'finance' : 'sales'
  );

  /* Filters — default to full current FY (Q1) through next FY (Q4) */
  const [selectedBU,     setSelectedBU]     = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState<string[]>([]);
  const [selectedDept,   setSelectedDept]   = useState<string[]>([]);
  const [startYear,    setStartYear]    = useState(currentYear);
  const [startQuarter, setStartQuarter] = useState(1);
  const [endYear,      setEndYear]      = useState(currentYear + 1);
  const [endQuarter,   setEndQuarter]   = useState(4);
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* UI state */
  const [filterOpen,       setFilterOpen]       = useState(false);
  const [searchStr,        setSearchStr]        = useState('');
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>([]);
  const [showChart,        setShowChart]        = useState(true);

  /* buildPayload — useCallback so handleSearch has stable deps */
  const buildPayload = useCallback(() => ({
    businessUnits: selectedBU,
    clients:       selectedClient,
    departments:   selectedDept,
    startYear,
    startQuarter,
    endYear,
    endQuarter,
    page:     page - 1,
    size:     pageSize,
  }), [selectedBU, selectedClient, selectedDept, startYear, startQuarter, endYear, endQuarter, page, pageSize]);

  const handleSearch = useCallback(() => {
    const payload = buildPayload();
    if (activeView === 'finance' && isFinanceRole) {
      dispatch(fetchForecastRevenue(payload)).then((res: any) => {
        if (res.type !== 'revenueForecast/fetchForecastRevenue/fulfilled') {
          rejectedApiMessage(res.payload);
        }
      });
    } else if (activeView === 'sales' && isSalesRole) {
      dispatch(fetchSalesViewRevenue(payload)).then((res: any) => {
        if (res.type !== 'revenueForecast/fetchSalesViewRevenue/fulfilled') {
          rejectedApiMessage(res.payload);
        }
      });
    }
  }, [buildPayload, activeView, isFinanceRole, isSalesRole, dispatch]);

  /* Load filter options once */
  useEffect(() => {
    dispatch(fetchFilterOptions()).then((res: any) => {
      if (res.type !== 'revenueForecast/fetchFilterOptions/fulfilled') {
        rejectedApiMessage(res.payload);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Initial data load */
  useEffect(() => {
    handleSearch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Re-fetch when view or pagination changes */
  useEffect(() => {
    handleSearch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, page, pageSize]);

  const handleReset = () => {
    setSelectedBU([]);
    setSelectedClient([]);
    setSelectedDept([]);
    setStartYear(currentYear);
    setStartQuarter(1);
    setEndYear(currentYear + 1);
    setEndQuarter(4);
    setPage(1);
  };

  const handleExport = () => {
    if (exportLoading) return;
    const payload = buildPayload();
    const action = activeView === 'finance' ? exportFinanceViewAsExcel : exportSalesViewAsExcel;
    dispatch(action(payload as any)).then((res: any) => {
      const fulfilled = activeView === 'finance'
        ? 'revenueForecast/exportFinanceViewAsExcel/fulfilled'
        : 'revenueForecast/exportSalesViewAsExcel/fulfilled';
      if (res.type === fulfilled) {
        const blob = new Blob([res.payload], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `revenue-forecast-${activeView}-${Date.now()}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        notification.success({ message: 'Export successful' });
      } else {
        rejectedApiMessage(res.payload);
      }
    });
  };

  /* ── Transform + extract structured data ──────────────────
     transformOldToNewStructure handles BOTH the legacy flat format
     and the current hierarchical format (returns as-is if already new). */
  const transformedFinance: any = forecastData ? transformOldToNewStructure(forecastData as any) : null;
  const transformedSales:   any = salesData    ? transformOldToNewStructure(salesData as any)    : null;
  const rawData       = activeView === 'finance' ? transformedFinance : transformedSales;
  const isLoading     = activeView === 'finance' ? loading     : salesLoading;
  const dataError     = activeView === 'finance' ? error       : salesError;

  const rf              = (rawData as any)?.revenueForecast;
  const totalRev        = rf?.TotalRevenue;
  const quarterList: QuarterEntry[]    = totalRev?.quarters   || [];
  const buList:      BUData[]          = rf?.businessUnits    || [];
  const clientList:  SalesClientData[] = rf?.clients          || [];
  const totalElements = (activeView === 'finance' ? forecastData : salesData as any)?.totalElements || 0;
  const totalPages    = (activeView === 'finance' ? forecastData : salesData as any)?.totalPages    || 1;

  /* KPI values */
  const totalForecast  = totalRev?.total    || 0;
  const totalActuals   = totalRev?.actuals  || 0;
  const totalProjects  = totalRev?.projects || 0;
  const totalPipeline  = totalRev?.pipeline || 0;
  const realizationPct = totalForecast > 0 ? (totalActuals / totalForecast) * 100 : 0;

  const allQuarterKeys     = quarterList.map(q => q.quarter);
  const visibleQuarterCols = selectedQuarters.length
    ? quarterList.filter(q => selectedQuarters.includes(q.quarter))
    : quarterList;
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);

  /* Toggle quarter chip */
  const toggleQuarter = (q: string) => {
    setSelectedQuarters(prev =>
      prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]
    );
  };

  /* ─── Render ─────────────────────────────────── */
  return (
    <div className="rfpoc-page">

      {/* ── HERO ────────────────────────────────── */}
      <div className="rfpoc-hero">
        <div className="rfpoc-hero-top">
          <div className="rfpoc-title-block">
            <h1>
              <FundOutlined style={{ marginRight: 10, opacity: 0.9 }} />
              Revenue Forecast Dashboard
            </h1>
            <p>
              {activeView === 'finance'
                ? 'Finance View  ·  BU → Department → Client hierarchy'
                : 'Sales View  ·  Client → BU → Department hierarchy'}
              &nbsp;·&nbsp; FY {currentYear} Q{currentQuarter} (Current)
            </p>
          </div>
          <div className="rfpoc-hero-actions">
            {/* View Toggle (only for Admin / Superuser) */}
            {isAdminOrSuperuser && (
              <div className="rfpoc-view-toggle">
                <button
                  className={`rfpoc-view-btn ${activeView === 'finance' ? 'active' : ''}`}
                  onClick={() => { setActiveView('finance'); setPage(1); }}
                >
                  <BarChartOutlined /> Finance
                </button>
                <button
                  className={`rfpoc-view-btn ${activeView === 'sales' ? 'active' : ''}`}
                  onClick={() => { setActiveView('sales'); setPage(1); }}
                >
                  <TrophyOutlined /> Sales
                </button>
              </div>
            )}
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterOpen(true)}
              style={{ borderRadius: 8, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}
            >
              Filters
              {(selectedBU.length + selectedClient.length + selectedDept.length) > 0 && (
                <Tag color="#fff" style={{ marginLeft: 4, color: '#764ba2', fontWeight: 700, fontSize: 10, padding: '0 5px' }}>
                  {selectedBU.length + selectedClient.length + selectedDept.length}
                </Tag>
              )}
            </Button>
          </div>
        </div>

        {/* KPI Cards Strip */}
        <div className="rfpoc-kpi-strip">
          <KPICard
            label="Total Forecast"
            value={fmt(totalForecast)}
            sub={fmtFull(totalForecast)}
            icon={<DollarOutlined style={{ color: '#fff' }} />}
            iconBg="linear-gradient(135deg,#667eea,#764ba2)"
            loading={isLoading && !rawData}
          />
          <KPICard
            label="Actuals (YTD)"
            value={fmt(totalActuals)}
            sub={`Realization: ${realizationPct.toFixed(1)}%`}
            icon={<RiseOutlined style={{ color: '#fff' }} />}
            iconBg="linear-gradient(135deg,#f59e0b,#d97706)"
            badge={realizationPct > 0 ? { text: `${realizationPct.toFixed(1)}%`, type: realizationBadge(realizationPct) as any } : undefined}
            loading={isLoading && !rawData}
          />
          <KPICard
            label="Projects Revenue"
            value={fmt(totalProjects)}
            sub="Confirmed orders"
            icon={<AppstoreOutlined style={{ color: '#fff' }} />}
            iconBg="linear-gradient(135deg,#6366f1,#4f46e5)"
            loading={isLoading && !rawData}
          />
          <KPICard
            label="Pipeline Revenue"
            value={fmt(totalPipeline)}
            sub="Opportunities"
            icon={<BarChartOutlined style={{ color: '#fff' }} />}
            iconBg="linear-gradient(135deg,#10b981,#059669)"
            loading={isLoading && !rawData}
          />
          <KPICard
            label="Total Entities"
            value={isLoading ? '—' : String(totalElements || (activeView === 'finance' ? buList.length : clientList.length))}
            sub={activeView === 'finance' ? 'Business Units' : 'Clients (this page)'}
            icon={<TableOutlined style={{ color: '#fff' }} />}
            iconBg="linear-gradient(135deg,#64748b,#475569)"
            loading={isLoading && !rawData}
          />
        </div>

        {/* Summary Ribbon */}
        <div className="rfpoc-summary-ribbon">
          {quarterList.map(q => (
            <React.Fragment key={q.quarter}>
              <div className="rfpoc-ribbon-item">
                <div>
                  <div className="rfpoc-ribbon-label">
                    {q.quarter} {q.status === 'Current' && <span style={{ color: '#667eea', fontWeight: 700 }}>(NOW)</span>}
                  </div>
                  <div className="rfpoc-ribbon-value">{fmt(q.total)}</div>
                </div>
              </div>
              <div className="rfpoc-ribbon-divider" />
            </React.Fragment>
          ))}
          {quarterList.length === 0 && isLoading && (
            <Skeleton.Input active size="small" style={{ width: 600 }} />
          )}
        </div>
      </div>

      {/* ── BODY ────────────────────────────────── */}
      <div className="rfpoc-body">

        {/* Toolbar */}
        <div className="rfpoc-toolbar">
          <div className="rfpoc-quarter-chips">
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>QUARTER:</span>
            {allQuarterKeys.map(q => {
              const qObj = quarterList.find(qq => qq.quarter === q);
              return (
                <div
                  key={q}
                  className={`rfpoc-qchip ${selectedQuarters.includes(q) ? 'active' : ''} ${qObj?.status === 'Current' ? 'current-quarter' : ''}`}
                  onClick={() => toggleQuarter(q)}
                >
                  {q}
                  {qObj?.status === 'Current' && !selectedQuarters.includes(q) && (
                    <span className="rfpoc-q-curr-mark" />
                  )}
                </div>
              );
            })}
            {selectedQuarters.length > 0 && (
              <div className="rfpoc-qchip" onClick={() => setSelectedQuarters([])}>
                <CloseOutlined style={{ fontSize: 9, marginRight: 3 }} />Clear
              </div>
            )}
          </div>
          <div className="rfpoc-toolbar-right">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleSearch}
              loading={isLoading}
              type="primary"
              style={{ borderRadius: 8, background: 'linear-gradient(135deg,#667eea,#764ba2)', border: 'none' }}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Main Table */}
        <div className="rfpoc-table-section">
          <div className="rfpoc-table-header-bar">
            <h3>
              {activeView === 'finance'
                ? <><BarChartOutlined style={{ color: '#667eea', marginRight: 8 }} />Finance View — BU / Department / Client</>
                : <><TrophyOutlined style={{ color: '#10b981', marginRight: 8 }} />Sales View — Client / BU / Department</>
              }
            </h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {(selectedBU.length > 0 || selectedClient.length > 0 || selectedDept.length > 0) && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {selectedBU.map(b => <Tag key={b} closable onClose={() => setSelectedBU(prev => prev.filter(x => x !== b))} color="purple">{b}</Tag>)}
                  {selectedClient.map(c => <Tag key={c} closable onClose={() => setSelectedClient(prev => prev.filter(x => x !== c))} color="green">{c}</Tag>)}
                  {selectedDept.map(d => <Tag key={d} closable onClose={() => setSelectedDept(prev => prev.filter(x => x !== d))} color="blue">{d}</Tag>)}
                </div>
              )}
            </div>
          </div>

          {/* Loading skeleton */}
          {isLoading && !rawData && (
            <div className="rfpoc-loading">
              <Spin size="large" />
              <p>Loading revenue forecast data…</p>
            </div>
          )}

          {/* Error state */}
          {!isLoading && dataError && (
            <div className="rfpoc-empty">
              <div className="rfpoc-empty-icon"><WarningOutlined /></div>
              <h4>Failed to load data</h4>
              <p>{dataError}</p>
              <Button type="primary" onClick={handleSearch} style={{ marginTop: 12, borderRadius: 8 }}>
                Retry
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !dataError && rawData && (activeView === 'finance' ? buList : clientList).length === 0 && (
            <div className="rfpoc-empty">
              <div className="rfpoc-empty-icon"><BarChartOutlined /></div>
              <h4>No data found</h4>
              <p>Try adjusting your filters or select a different period.</p>
            </div>
          )}

          {/* Table */}
          {(rawData || isLoading) && (activeView === 'finance' ? buList : clientList).length > 0 && (
            <div className="rfpoc-table-wrap" style={{ maxHeight: 560, overflowY: 'auto' }}>
              <table className="rfpoc-table">
                <thead>
                  {/* Row 1: fixed columns + quarter group labels */}
                  <tr>
                    <th className="sticky-left" rowSpan={2} style={{ minWidth: 260, verticalAlign: 'middle' }}>
                      {activeView === 'finance' ? 'Business Unit / Department / Client' : 'Client / BU / Department'}
                    </th>
                    <th rowSpan={2} style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                      <span style={{ color: '#5b21b6' }}>Projects</span>
                    </th>
                    <th rowSpan={2} style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                      <span style={{ color: '#065f46' }}>Pipeline</span>
                    </th>
                    <th rowSpan={2} style={{ textAlign: 'right', verticalAlign: 'middle' }}>Total Forecast</th>
                    <th rowSpan={2} style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                      <span style={{ color: '#92400e' }}>Actuals</span>
                    </th>
                    <th rowSpan={2} style={{ minWidth: 130, verticalAlign: 'middle' }}>Realization</th>
                    <th rowSpan={2} style={{ minWidth: 80, verticalAlign: 'middle' }}>Trend</th>
                    {visibleQuarterCols.map(q => (
                      <th key={q.quarter} colSpan={4}
                        style={{ textAlign: 'center', borderLeft: '2px solid #e2e8f0', background: q.status === 'Current' ? '#ede9fe' : undefined }}
                      >
                        {q.quarter}
                        {q.status === 'Current' && <span className="rfpoc-q-curr-mark" style={{ marginLeft: 4 }} />}
                      </th>
                    ))}
                  </tr>
                  {/* Row 2: sub-column labels per quarter */}
                  <tr>
                    {visibleQuarterCols.map(q => (
                      <React.Fragment key={q.quarter}>
                        <th style={{ textAlign: 'right', fontSize: 10, color: '#5b21b6', borderLeft: '2px solid #e2e8f0', whiteSpace: 'nowrap', minWidth: 72 }}>Projects</th>
                        <th style={{ textAlign: 'right', fontSize: 10, color: '#065f46', whiteSpace: 'nowrap', minWidth: 72 }}>Pipeline</th>
                        <th style={{ textAlign: 'right', fontSize: 10, color: '#1e293b', whiteSpace: 'nowrap', minWidth: 72 }}>Total</th>
                        <th style={{ textAlign: 'right', fontSize: 10, color: '#92400e', whiteSpace: 'nowrap', minWidth: 72 }}>Actuals</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Total Row */}
                  {totalRev && (
                    <tr className="row-total">
                      <td className="sticky-left">
                        <div className="rfpoc-name-cell">
                          <div style={{ width: 20 }} />
                          <span className="rfpoc-type-badge" style={{ background: '#1e293b', color: '#fff' }}>TOTAL</span>
                          <span className="rfpoc-name-text bu-level">All {activeView === 'finance' ? 'Business Units' : 'Clients'}</span>
                        </div>
                      </td>
                      <td className="rfpoc-rev-cell project-col">{fmt(totalRev.projects)}</td>
                      <td className="rfpoc-rev-cell pipeline-col">{fmt(totalRev.pipeline)}</td>
                      <td className="rfpoc-rev-cell total-col">{fmt(totalRev.total)}</td>
                      <td className="rfpoc-rev-cell actual-col">{fmt(totalRev.actuals)}</td>
                      <td className="rfpoc-real-cell">
                        <RealizationCell actual={totalRev.actuals} total={totalRev.total} />
                      </td>
                      <td className="rfpoc-sparkline-cell">
                        <Sparkline values={quarterList.map(q => q.total)} color="#764ba2" />
                      </td>
                      {visibleQuarterCols.map(q => (
                        <React.Fragment key={q.quarter}>
                          <td className="rfpoc-rev-cell project-col" style={{ borderLeft: '2px solid #e2e8f0' }}>{fmt(q.projectRevenue)}</td>
                          <td className="rfpoc-rev-cell pipeline-col">{fmt(q.pipelineRevenue)}</td>
                          <td className="rfpoc-rev-cell total-col">{fmt(q.total)}</td>
                          <td className="rfpoc-rev-cell actual-col">{fmt(q.actualRevenue)}</td>
                        </React.Fragment>
                      ))}
                    </tr>
                  )}

                  {/* Data Rows */}
                  {activeView === 'finance'
                    ? buList.map((bu, i) => (
                        <FinanceRows
                          key={`bu-${i}`}
                          bu={bu}
                          searchStr={searchStr}
                          selectedQuarters={selectedQuarters}
                        />
                      ))
                    : clientList.map((cl, i) => (
                        <SalesClientRow
                          key={`cl-${i}`}
                          client={cl}
                          searchStr={searchStr}
                          selectedQuarters={selectedQuarters}
                        />
                      ))
                  }
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination footer */}
          {rawData && totalElements > 0 && (
            <div className="rfpoc-footer">
              <span className="rfpoc-footer-info">
                Showing page {page} of {totalPages} · {totalElements} total {activeView === 'finance' ? 'BUs' : 'clients'}
              </span>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={totalElements}
                showSizeChanger
                pageSizeOptions={['5', '10', '20', '50']}
                onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
                size="small"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── FILTER DRAWER ─────────────────────────── */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FilterOutlined style={{ color: '#667eea' }} />
            <span>Advanced Filters</span>
          </div>
        }
        placement="right"
        width={Math.min(400, window.innerWidth - 16)}
        styles={{ body: { padding: '20px 16px', overflowX: 'hidden', overflowY: 'auto' } }}
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', boxSizing: 'border-box' }}>

          {isAdminOrSuperuser && (
            <div className="rfpoc-filter-item">
              <label>View Mode</label>
              <Select
                value={activeView}
                onChange={v => { setActiveView(v); setPage(1); }}
                style={{ borderRadius: 8 }}
              >
                <Option value="finance">Finance View (BU → Dept → Client)</Option>
                <Option value="sales">Sales View (Client → BU → Dept)</Option>
              </Select>
            </div>
          )}

          <div className="rfpoc-filter-item">
            <label>Business Units</label>
            <Select
              mode="multiple"
              value={selectedBU}
              onChange={setSelectedBU}
              placeholder="All BUs"
              maxTagCount={2}
              loading={filterOptionsLoading}
              style={{ borderRadius: 8 }}
              allowClear
            >
              {(filterOptions?.businessUnits || []).map(bu => (
                <Option key={bu} value={bu}>{bu}</Option>
              ))}
            </Select>
          </div>

          <div className="rfpoc-filter-item">
            <label>Clients</label>
            <Select
              mode="multiple"
              value={selectedClient}
              onChange={setSelectedClient}
              placeholder="All Clients"
              maxTagCount={2}
              loading={filterOptionsLoading}
              style={{ borderRadius: 8 }}
              allowClear
            >
              {(filterOptions?.clients || []).map(c => (
                <Option key={c} value={c}>{c}</Option>
              ))}
            </Select>
          </div>

          <div className="rfpoc-filter-item">
            <label>Departments</label>
            <Select
              mode="multiple"
              value={selectedDept}
              onChange={setSelectedDept}
              placeholder="All Departments"
              maxTagCount={2}
              loading={filterOptionsLoading}
              style={{ borderRadius: 8 }}
              allowClear
            >
              {(filterOptions?.departments || []).map(d => (
                <Option key={d} value={d}>{d}</Option>
              ))}
            </Select>
          </div>

          <div style={{ display: 'flex', gap: 8, width: '100%', boxSizing: 'border-box' }}>
            <div className="rfpoc-filter-item" style={{ flex: '1 1 0', minWidth: 0 }}>
              <label>Start Year</label>
              <Select value={startYear} onChange={setStartYear} style={{ width: '100%' }}>
                {yearOptions.map(y => <Option key={y} value={y}>{y}</Option>)}
              </Select>
            </div>
            <div className="rfpoc-filter-item" style={{ flex: '1 1 0', minWidth: 0 }}>
              <label>Start Quarter</label>
              <Select value={startQuarter} onChange={setStartQuarter} style={{ width: '100%' }}>
                {[1, 2, 3, 4].map(q => <Option key={q} value={q}>Q{q}</Option>)}
              </Select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, width: '100%', boxSizing: 'border-box' }}>
            <div className="rfpoc-filter-item" style={{ flex: '1 1 0', minWidth: 0 }}>
              <label>End Year</label>
              <Select value={endYear} onChange={setEndYear} style={{ width: '100%' }}>
                {yearOptions.map(y => <Option key={y} value={y}>{y}</Option>)}
              </Select>
            </div>
            <div className="rfpoc-filter-item" style={{ flex: '1 1 0', minWidth: 0 }}>
              <label>End Quarter</label>
              <Select value={endQuarter} onChange={setEndQuarter} style={{ width: '100%' }}>
                {[1, 2, 3, 4].map(q => <Option key={q} value={q}>Q{q}</Option>)}
              </Select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              type="primary"
              block
              onClick={() => { setPage(1); setFilterOpen(false); setTimeout(() => handleSearch(), 0); }}
              style={{ borderRadius: 8, background: 'linear-gradient(135deg,#667eea,#764ba2)', border: 'none' }}
            >
              Apply Filters
            </Button>
            <Button block onClick={() => { handleReset(); setTimeout(() => handleSearch(), 0); }} style={{ borderRadius: 8 }}>
              Reset
            </Button>
          </div>

          {/* Active filter summary */}
          {(selectedBU.length + selectedClient.length + selectedDept.length) > 0 && (
            <div style={{ padding: '12px', background: '#f0f4ff', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>
                ACTIVE FILTERS
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {selectedBU.map(b => (
                  <Tag key={b} color="purple" closable onClose={() => setSelectedBU(prev => prev.filter(x => x !== b))}>
                    {b}
                  </Tag>
                ))}
                {selectedClient.map(c => (
                  <Tag key={c} color="green" closable onClose={() => setSelectedClient(prev => prev.filter(x => x !== c))}>
                    {c}
                  </Tag>
                ))}
                {selectedDept.map(d => (
                  <Tag key={d} color="blue" closable onClose={() => setSelectedDept(prev => prev.filter(x => x !== d))}>
                    {d}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default RevenueForecastingPOC;
